// Запросы журнала здоровья: /api/blood-pressure, /wellbeing, /conditions, /labs, /supplements, /nutrition.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type BloodPressure = {
  id: string
  measured_at: string
  day: string
  systolic: number
  diastolic: number
  pulse: number | null
  arm: 'left' | 'right' | null
  note: string | null
  above_normal: boolean
}
export type BloodPressureInput = {
  systolic: number | null
  diastolic: number | null
  pulse: number | null
  arm: 'left' | 'right' | null
  note: string | null
  measured_at: string | null
}

export type Wellbeing = { id: string; day: string; wellbeing: number; energy: number | null; note: string | null }
export type WellbeingInput = { wellbeing: number; energy: number | null; note: string | null }

export type ConditionKind = 'injury' | 'illness' | 'pain'
export type Condition = {
  id: string
  kind: ConditionKind
  body_area: string | null
  title: string
  started_on: string
  ended_on: string | null
  severity: number
  note: string | null
  active: boolean
}
export type ConditionInput = {
  kind: ConditionKind
  title: string
  started_on: string
  body_area: string | null
  severity: number
  note: string | null
}

export type Lab = {
  id: string
  taken_on: string
  lab: string | null
  marker: string
  value: number
  unit: string
  ref_low: number | null
  ref_high: number | null
  note: string | null
  out_of_range: boolean
}
export type LabRowInput = {
  marker: string
  value: number | null
  unit: string
  ref_low: number | null
  ref_high: number | null
}
export type LabBatchInput = { taken_on: string; lab: string | null; results: LabRowInput[] }

export type Supplement = {
  id: string
  name: string
  dose: string | null
  frequency: string | null
  started_on: string | null
  ended_on: string | null
  note: string | null
  active: boolean
  taken: boolean
  adherence_30d: number
}
export type SupplementInput = {
  name: string
  dose: string | null
  frequency: string | null
  started_on: string | null
  ended_on: string | null
}

export type NutritionPoint = {
  day: string
  id: string | null
  kcal: number | null
  protein_g: number | null
  fat_g: number | null
  carbs_g: number | null
  water_ml: number | null
  note: string | null
  burned_kcal: number | null
}
export type NutritionInput = {
  kcal: number | null
  protein_g: number | null
  fat_g: number | null
  carbs_g: number | null
  water_ml: number | null
  note: string | null
}

export const journalKeys = {
  pressure: ['health', 'blood-pressure'] as const,
  wellbeing: ['health', 'wellbeing'] as const,
  conditions: ['health', 'conditions'] as const,
  areas: ['health', 'body-areas'] as const,
  labs: ['health', 'labs'] as const,
  markers: ['health', 'lab-markers'] as const,
  supplements: (day: string) => ['health', 'supplements', day] as const,
  nutrition: ['health', 'nutrition'] as const,
}

/** Любая запись сбрасывает все запросы журнала: данных немного, согласованность важнее. */
function useJournalMutation<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ['health'] }),
  })
}

// --- давление
export const usePressure = () =>
  useQuery({ queryKey: journalKeys.pressure, queryFn: () => api.get<BloodPressure[]>('/blood-pressure') })
export const useAddPressure = () =>
  useJournalMutation((data: BloodPressureInput) => api.post<BloodPressure>('/blood-pressure', data))
export const useDeletePressure = () =>
  useJournalMutation((id: string) => api.delete(`/blood-pressure/${id}`))

// --- самочувствие и состояния
export const useWellbeing = () =>
  useQuery({ queryKey: journalKeys.wellbeing, queryFn: () => api.get<Wellbeing[]>('/wellbeing') })
export const useSaveWellbeing = () =>
  useJournalMutation((v: { day: string; data: WellbeingInput }) =>
    api.put<Wellbeing>(`/wellbeing/${v.day}`, v.data),
  )
export const useDeleteWellbeing = () => useJournalMutation((day: string) => api.delete(`/wellbeing/${day}`))

export const useConditions = () =>
  useQuery({ queryKey: journalKeys.conditions, queryFn: () => api.get<Condition[]>('/conditions') })
export const useBodyAreas = () =>
  useQuery({
    queryKey: journalKeys.areas,
    queryFn: () => api.get<Record<string, string>>('/conditions/areas'),
    staleTime: Infinity,
  })
export const useAddCondition = () =>
  useJournalMutation((data: ConditionInput) => api.post<Condition>('/conditions', data))
export const useCloseCondition = () =>
  useJournalMutation((v: { id: string; ended_on: string | null }) =>
    api.post<Condition>(`/conditions/${v.id}/close`, { ended_on: v.ended_on }),
  )
export const useDeleteCondition = () =>
  useJournalMutation((id: string) => api.delete(`/conditions/${id}`))

// --- анализы
export const useLabs = () => useQuery({ queryKey: journalKeys.labs, queryFn: () => api.get<Lab[]>('/labs') })
export const useLabMarkers = () =>
  useQuery({ queryKey: journalKeys.markers, queryFn: () => api.get<string[]>('/labs/markers') })
export const useAddLabs = () => useJournalMutation((data: LabBatchInput) => api.post<Lab[]>('/labs', data))
export const useDeleteLab = () => useJournalMutation((id: string) => api.delete(`/labs/${id}`))

// --- добавки
export const useSupplements = (day: string) =>
  useQuery({
    queryKey: journalKeys.supplements(day),
    queryFn: () => api.get<Supplement[]>('/supplements', { query: { day } }),
  })
export const useAddSupplement = () =>
  useJournalMutation((data: SupplementInput) => api.post<Supplement>('/supplements', data))
export const useDeleteSupplement = () =>
  useJournalMutation((id: string) => api.delete(`/supplements/${id}`))
export const useSetIntake = () =>
  useJournalMutation((v: { id: string; day: string; taken: boolean }) =>
    v.taken
      ? api.put<Supplement>(`/supplements/${v.id}/intake/${v.day}`)
      : api.delete<Supplement>(`/supplements/${v.id}/intake/${v.day}`),
  )

// --- питание
export const useNutrition = () =>
  useQuery({ queryKey: journalKeys.nutrition, queryFn: () => api.get<NutritionPoint[]>('/nutrition') })
export const useSaveNutrition = () =>
  useJournalMutation((v: { day: string; data: NutritionInput }) => api.put(`/nutrition/${v.day}`, v.data))
export const useDeleteNutrition = () => useJournalMutation((day: string) => api.delete(`/nutrition/${day}`))
